<template>
  <div class="plan-selection-list-container">
    <h3>Plans</h3>
    <template v-if="relatedPlans.length">
      <div v-if="relatedPlans.length > 3" class="p-mb-2 p-d-flex p-jc-end">
        <Button
          :label="localSelectedPlanIntIds.length === relatedPlans.length ? 'Deselect All' : 'Select All'"
          class="p-button-text p-button-sm"
          @click="onSelectAllChange"
        />
      </div>
      <div
        v-for="(plan) of relatedPlans"
        :key="'plan_' + plan.id.intID"
      >
        <template>
          <div class="p-mb-2 p-d-flex p-ai-start">
            <Checkbox
                :id="'plan_' + plan.id.intID"
                :value="plan.id.intID"
                v-model="localSelectedPlanIntIds"
                class="p-mr-3" 
              />
            <label :for="'plan_' + plan.id.intID">
              <template v-if="plan.isNested">
                {{ plan.abbreviatedName }}  |  {{ plan.name }}
              </template>
              <template v-else>
                {{ plan.name }}
              </template>
            </label>
          </div>
        </template>
      </div>
      <Button 
        class="p-mt-6"
        label="Distribute Tactic"
        @click="copyTacticToPlans"
        />
    </template>
    <template v-else>
      <p>No plans available for distribution.</p>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ID from '@/models/ID'
import Plan from '@/models/Plan'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Tactic from '@/models/Tactic'
import Role from '@/models/Role'

Vue.component('Checkbox', Checkbox)
Vue.component('Button', Button)

export default Vue.extend({
  name: 'DistributeTacticModal',
  props: {
    tacticId: {
      type: ID,
      required: true,
    },
  },
  data: () => {
    return {
      localSelectedPlanIntIds: [] as number[],
    }
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    relatedPlans(): Plan[] {
      let returnArray = [] as Plan[]

      if(this.currentPlan.isLead){
        // Show all nested plans
        returnArray = this.$store.getters.allPlans.filter((plan) => this.currentPlan.plans.filter((id) => id.intID === plan.id.intID).length)
      }else{
        // Show sibling plans for this nested plan, don't allow distributing from nested in to lead plan
        const leadPlan = this.$store.getters.allPlans.find((plan) => plan.id.intID === this.currentPlan.parentId.intID)
        returnArray = this.$store.getters.allPlans.filter(
          (plan) => leadPlan.plans.filter(
            (id) => id.intID === plan.id.intID
          ).length
        )
      }

      // Exclude current plan and the tactic's own plan
      returnArray = returnArray.filter((plan) => {
        if (plan.id.intID === this.currentPlan.id.intID) return false
        if (this.tacticForTacticId && plan.id.intID === this.tacticForTacticId.planId.intID) return false
        return true
      })

      // Check user's role in target plans
      returnArray = returnArray.filter((plan) => {
        // Super admins may not have a role row in the target plan
        if (this.$store.getters.isCurrentUserSuperAdmin) return true

        const userRoleForPlanAndCurrentUser = this.$store.getters.currentAccount.userRoles.find((userRole) => {
          return userRole.userId.intID === this.$store.getters.currentUser.id.intID && userRole.planId.intID === plan.id.intID
        })

        if(userRoleForPlanAndCurrentUser){
          if(userRoleForPlanAndCurrentUser.role.level < Role.LEVEL_VIEWER){
            // User is contributor or admin in target plan
            return true
          }else{
            // User is viewer in target plan
            return false
          }
        }
        return false
      })

      return returnArray
    },
    tacticForTacticId(): Tactic | undefined {
      return this.currentPlan.tactics.find((tactic) => tactic.id.intID === this.tacticId.intID)
    },
  },
  methods: {
    async copyTacticToPlans() {
      const tacticToCopy = this.currentPlan.tactics.find((tactic) => tactic.id.intID === this.tacticId.intID)
      if(tacticToCopy){
        let tacticsCreated = 0

        // Fetch summary content once before copying — summary HTML lives at a separate URL
        let summaryContent: string | null = null
        if (tacticToCopy.tacticSummary?.contentUrl) {
          summaryContent = await this.$store.getters.services.tactics
            .fetchSummaryContent(tacticToCopy.tacticSummary.contentUrl)
            .catch(() => null)
        }

        this.localSelectedPlanIntIds.forEach(async (intId) => {
          const thisPlan = this.$store.getters.allPlans.find((plan) => plan.id.intID === intId)
          const newTactic = new Tactic(
            new ID(),
            this.$store.getters.currentUser.id,
            tacticToCopy.title,
            tacticToCopy.startDate,
            tacticToCopy.endDate,
            tacticToCopy.tacticTypeId,
            tacticToCopy.estimatedCost,
            tacticToCopy.actualCost,
            tacticToCopy.impressions,
            tacticToCopy.ratingPoints,
            tacticToCopy.tags.filter((tag) => tag.isLead).map((tag) => tag.id),
            [], // TODO: do we need to copy typeValues to the target plan?
            tacticToCopy.relatedInitiatives.filter((initiativeMapping) => this.currentPlan.initiatives.find((initiative) => initiative.id.intID === initiativeMapping.initiativeId.intID)?.isLead),
            tacticToCopy.isFlagged,
            ID.fromResponseObject(intId, 'plans'),
            tacticToCopy.isAllDay,
            tacticToCopy.repeats,
            tacticToCopy.recurrenceEndDate,
            tacticToCopy.isCustomRecurrence,
            tacticToCopy.recurrenceText,
            tacticToCopy.rrule,
            tacticToCopy.tacticPlatformIds,
            tacticToCopy.isNested,
            tacticToCopy.isLead,
            tacticToCopy.documentIds,
            // tacticToCopy.localIsInitiative,
            '', // Summary is created via the summary API after tactic creation
            tacticToCopy.isUnscheduled,

            tacticToCopy.channelName,
            tacticToCopy.tacticTypeName,
            tacticToCopy.isSubscriptionEvent,
            tacticToCopy.abbreviatedPlanName,
            tacticToCopy.tags.filter((tag) => tag.isLead),
            tacticToCopy.tacticPlatforms,
            [], // TODO: do we need to copy documents to the target plan?
            tacticToCopy.originalTacticID,
            tacticToCopy.isRecurringEventClone,
          )

          try {
            await this.$store.getters.services.tactics
              .create(newTactic)
              .then(
                async (newTacticFromServer) => {
                  // Add new tactic to current plan if needed
                  if(this.currentPlan.isLead){
                    newTacticFromServer.isNested = true
                    this.$store.dispatch('addTacticToCurrentPlan', newTacticFromServer)
                  } else if(thisPlan.isLead) {
                    newTacticFromServer.isLead = true
                    this.$store.dispatch('addTacticToCurrentPlan', newTacticFromServer)
                  }

                  if (summaryContent) {
                    newTacticFromServer.summary = summaryContent
                    await this.$store.getters.services.tactics
                      .createTacticSummary(newTacticFromServer.id.apiID, summaryContent)
                      .catch(() => { /* Summary copy failed but tactic was created */ })
                  }

                  tacticsCreated++
                },
                (error) => {
                  Vue.prototype.$toast.add({
                    severity: AlertMessageSeverity.error,
                    summary: `Error copying tactic to ${thisPlan.name}. (1)`,
                    life: 3000,
                  })
                }
              )
          } catch (error: any) {
            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.error,
              summary: `Error copying tactic to ${thisPlan.name}. (2)`,
              life: 3000,
            })
          }
        })

        if(tacticsCreated > 0){
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.success,
            summary: 'Tactic copied.',
            life: 3000,
          })
        }
      }else{
        Vue.prototype.$toast.add({
          severity: AlertMessageSeverity.error,
          summary: 'Tactic not found.',
          life: 3000,
        })
      }

      this.$emit('hide')
    },
    onSelectAllChange() {
      if(this.localSelectedPlanIntIds.length === this.relatedPlans.length){
        this.localSelectedPlanIntIds = []
      }else{
        this.localSelectedPlanIntIds = this.relatedPlans.map((plan) => plan.id.intID)
      }
    }
  },
})
</script>

<style lang="scss">
.plan-selection-list-container {
  .p-checkbox-checked {
    .p-checkbox-box {
      background: #EFEFEF;
    }
    .p-checkbox-icon::before {
      content: "\e909";
      font-family: 'primeicons';
    }
  }
  .p-checkbox-disabled + label {
    opacity: 0.4;
  }
}
</style>
